import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { Copy, Sparkles, Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";
import BrandLogo from "@/components/BrandLogo";
import SEO from "@/components/SEO";

export default function MyVouchers() {
    const [vouchers, setVouchers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [revealed, setRevealed] = useState({});

    useEffect(() => {
        (async () => {
            const r = await api.get("/vouchers/my");
            setVouchers(r.data.vouchers);
            setLoading(false);
        })();
    }, []);

    const copy = (t) => {
        navigator.clipboard.writeText(t);
        toast.success("Copied!");
    };

    return (
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 lg:px-12 py-10">
            <SEO title="My Vouchers" description="Your purchased gift cards on LoyltyKart." path="/vouchers" noindex />
            <h1 className="font-serif text-4xl md:text-5xl mb-6">My Vouchers</h1>
            {loading ? (
                <p className="text-[#6C6F7A]">Loading…</p>
            ) : vouchers.length === 0 ? (
                <div className="py-20 text-center bg-white border border-[#E5E0D5] rounded-2xl">
                    <Sparkles className="w-12 h-12 mx-auto text-[#E5E0D5] mb-3" />
                    <p className="font-serif text-2xl">No vouchers yet</p>
                    <Link to="/catalog" className="rk-btn-primary inline-block mt-5 !text-sm">Get your first gift card</Link>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-4">
                    {vouchers.map((v) => (
                        <div
                            key={v.id}
                            data-testid={`voucher-${v.id}`}
                            className="relative bg-white border border-[#E5E0D5] rounded-2xl p-5 overflow-hidden"
                        >
                            <div className="absolute top-0 right-0 w-32 h-32 rk-gradient-gold opacity-5 rounded-full -translate-y-1/2 translate-x-1/2" />
                            <div className="flex gap-4 relative">
                                <div className="w-16 h-16 rk-logo-box flex items-center justify-center shrink-0">
                                    <BrandLogo brand={v} size="sm" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[10px] uppercase tracking-wider text-[#6C6F7A]">{v.category}</p>
                                    <p className="font-semibold truncate">{v.brand_name}</p>
                                    <p className="font-serif text-2xl text-[#0B1437]">{formatINR(v.denomination)}</p>
                                </div>
                                <button
                                    onClick={() => setRevealed({ ...revealed, [v.id]: !revealed[v.id] })}
                                    className="p-2 text-[#6C6F7A] hover:text-[#0B1437]"
                                >
                                    {revealed[v.id] ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                            <div className="mt-4 bg-[#EFECE2] rounded-xl p-3 font-mono text-sm relative">
                                <div className="text-[10px] uppercase tracking-wider text-[#6C6F7A] mb-1">Voucher Code</div>
                                {revealed[v.id] ? (
                                    <div className="flex items-center justify-between gap-2">
                                        <div>
                                            <div className="font-bold text-[#0B1437]">{v.code}</div>
                                            <div className="text-xs text-[#6C6F7A]">PIN: {v.pin}</div>
                                        </div>
                                        <button data-testid={`copy-${v.id}`} onClick={() => copy(`${v.code} PIN:${v.pin}`)} className="text-[#0B1437]">
                                            <Copy className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="text-[#6C6F7A] tracking-[0.4em] text-lg">•••• •••• •••• ••••</div>
                                )}
                            </div>
                            <div className="mt-3 flex items-center justify-between text-xs text-[#6C6F7A]">
                                <span>Valid {v.validity || "1 Year"}</span>
                                <span className="text-[#1D5C42] font-semibold">{v.status}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
